/**
 * NextPrayerWidget — compact "Prochaine prière" strip for the Home page
 * and any other page where we want to surface the next prayer time.
 *
 * Behaviour:
 *   - Reads location/method/school from local prefs (no network unless
 *     the user is logged in and remote prefs are richer).
 *   - If no location, renders a soft call-to-action linking to the
 *     prayer-times setup page.
 *   - Ticks every second to keep the countdown live.
 *
 * Lightweight: only fetches today's timings (with on-disk cache fallback
 * via prayerTimesService) and reuses computeNextPrayer/formatCountdown.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sun, MapPin, Compass, ChevronRight } from 'lucide-react';
import {
  prayerTimesService, computeNextPrayer, formatCountdown,
} from '../../services/prayerTimesService';

const NextPrayerWidget = ({ className = '', compact = false }) => {
  const [prefs] = useState(() => prayerTimesService.getLocalPrefs());
  const [timings, setTimings] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [locLabel, setLocLabel] = useState(prefs.last_location?.label);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const loc = prefs.last_location;
    if (!loc?.lat || !loc?.lng) return;
    let cancelled = false;
    prayerTimesService.getTimings({
      lat: loc.lat, lng: loc.lng,
      method: prefs.method ?? 12,
      school: prefs.school ?? 0,
    })
      .then((r) => { if (!cancelled) setTimings(r.timings); })
      .catch(() => { /* offline / network */ });
    setLocLabel(loc.label);
    return () => { cancelled = true; };
  }, [prefs]);

  const next = useMemo(() => computeNextPrayer(timings, now), [timings, now]);

  if (!prefs.last_location) {
    return (
      <Link
        to="/prieres/horaires"
        className={`flex items-center justify-between gap-3 p-3 rounded-lg
                    border border-border bg-surface-elevated/40 hover:bg-surface-elevated
                    transition-colors ${className}`}
        data-testid="next-prayer-widget-empty"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sun className="w-5 h-5 text-primary shrink-0" />
          <div className="text-sm text-text-primary">
            Activez vos horaires de prière
            <span className="block text-xs text-text-muted">Géolocalisation ou recherche par ville</span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-text-muted shrink-0" />
      </Link>
    );
  }

  if (!next) {
    return (
      <div className={`p-3 rounded-lg border border-border bg-surface-elevated/40 ${className}`}>
        <div className="flex items-center gap-2 text-sm text-text-muted">
          <Sun className="w-4 h-4 animate-pulse" />
          Chargement des horaires…
        </div>
      </div>
    );
  }

  return (
    <Link
      to="/prieres/horaires"
      className={`group flex items-center justify-between gap-3 p-3 rounded-lg
                  border border-primary/30 bg-primary/5 hover:bg-primary/10
                  transition-colors ${className}`}
      data-testid="next-prayer-widget"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Sun className="w-5 h-5 text-primary shrink-0" />
        <div className="min-w-0">
          <div className="text-xs text-text-muted uppercase tracking-wider">Prochaine prière</div>
          <div className="font-serif text-base text-text-primary truncate">
            {next.label} <span className="text-text-muted font-mono">à {next.at.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          {!compact && locLabel && (
            <div className="text-[11px] text-text-muted flex items-center gap-1">
              <MapPin className="w-3 h-3" />{locLabel}
            </div>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10px] text-text-muted uppercase tracking-wider">Dans</div>
        <div className="font-mono text-base text-primary tabular-nums" data-testid="next-prayer-widget-countdown">
          {formatCountdown(next.msUntil)}
        </div>
      </div>
    </Link>
  );
};

export default NextPrayerWidget;
